///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { PlannerEulaDTO } from '../../../generated/shared-types.model';
import { Store } from '@ngrx/store';
import { selectEula } from '../../../shared/store/eula/eula.selectors';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Principal } from '../../../shared/auth/principal.service';
import { acceptEula, checkAcceptEula, getEula } from '../../../shared/store/eula/eula.actions';
import { SuiTranslatePipe } from '@siklu/sui-lib/localization';
import { SuiSvgIconComponent } from '@siklu/sui-lib/components/svg-icon';
import { SuiLoadingDirective } from '@siklu/sui-lib';
import { ButtonsDirectivesModule } from '../../../shared/directives/buttons-directives/buttons-directives.module';
import { AsyncPipe, DatePipe } from '@angular/common';

@Component({
    selector: 'winde-accept-eula-dialog',
    templateUrl: './accept-eula-dialog.component.html',
    styleUrls: ['../eula-dialogs.scss'],
    imports: [SuiTranslatePipe, SuiSvgIconComponent, SuiLoadingDirective, MatDialogModule, ButtonsDirectivesModule, DatePipe, AsyncPipe]
})
export class AcceptEulaDialogComponent implements OnInit, OnDestroy {
    public static readonly DIALOG_CONFIG: MatDialogConfig = {
        width: '500px',
        height: '230px',
        position: { top: '30px' },
        disableClose: true
    };

    public eula$: Observable<PlannerEulaDTO> = inject(Store).select(selectEula);
    public readonly loaderKey: LoaderIds = LoaderIds.ACCEPT_EULA_ID;
    public readonly downloadUrl: string = '/api/eula/file';
    public readonly preloaderService = inject(PreloaderService);

    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);
    private readonly dialogRef = inject(MatDialogRef<AcceptEulaDialogComponent>);
    private readonly principal = inject(Principal);

    private isEulaAccepted = false;

    constructor() {
        this.actions$.pipe(ofType(checkAcceptEula)).subscribe((action) => {
            this.isEulaAccepted = action.isAccept;
            this.dialogRef.close();
        });
    }

    @HostListener('document:visibilitychange', [])
    onVisibilityChange() {
        this.handleEulaRequiring();
    }

    public ngOnInit(): void {
        this.store.dispatch(getEula());
    }

    public ngOnDestroy(): void {
        if (!this.isEulaAccepted) {
            this.principal.forceLogout();
        }
    }

    public accept(): void {
        this.store.dispatch(acceptEula());
    }

    public close(): void {
        this.store.dispatch(checkAcceptEula({ isAccept: false }));
    }

    private handleEulaRequiring(): void {
        if (document.visibilityState === 'visible') {
            this.principal.identity(true).then((user) => {
                if (user && !user?.eulaAcceptanceRequired) {
                    this.isEulaAccepted = true;
                    this.dialogRef.close();
                }
            });
        }
    }
}
